import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    MenuItem,
    IconButton,
    Pagination,
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    CircularProgress,
    Chip,
    useMediaQuery,
    useTheme,
    Collapse,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    FormControl,
    Select,
    Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HearingDialog from './HearingDialog';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import toast from 'react-hot-toast';
import CaseDetailsDialog from './CaseDetailsDialog';
import HearingDetailsDialog from './HearingDetailsDialog';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadCaseReportPdf from '../../Components/Download/DownloadCaseReportPdf';
// Priority Colors Helper
const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
        case 'high': return 'error';
        case 'medium': return 'warning';
        case 'low': return 'info';
        default: return 'default';
    }
};

// --- SINGLE ROW COMPONENT FOR DESKTOP (COLLAPSIBLE) ---
const Row = ({ row, index, onEdit, onAddHearing, onEditHearing, handleFullDetailsView, onViewHearingDetails, handlePDFDownload }) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>

                {/* 1. Main Row S.No */}
                <TableCell sx={{ fontWeight: 'bold' }}>{index + 1}</TableCell>

                <TableCell sx={{ fontWeight: 'medium' }}>{row.case_no}</TableCell>
                <TableCell>{row.fir_no || 'N/A'}</TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: '250px', py: 0.5 }}>
                        {row.client_names && row.client_names.map((name, i) => (
                            <Box key={i} sx={{ borderBottom: i < row.client_names.length - 1 ? '1px dashed #e0e0e0' : 'none', pb: 0.5 }}>
                                <Typography variant="body2" fontWeight="bold">{name}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {row.client_emails?.[i] || ''}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </TableCell>
                <TableCell>{row.court_name}</TableCell>
                <TableCell>{row.opposite_party || 'N/A'}</TableCell>
                {/* <TableCell>
                    <Chip label={row.priority} size="small" color={getPriorityColor(row.priority)} />
                </TableCell> */}
                <TableCell>
                    <Chip label={row.status} size="small" variant="outlined" color="primary" />
                </TableCell>
                <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <IconButton onClick={() => onEdit(row)} color="primary" size="small" title="Edit Case">
                            <EditIcon fontSize="small" />
                        </IconButton>
                        {/* <IconButton onClick={() => onAddHearing(row)} color="primary" size="small" title="Add Hearing">
                            <AddCircleOutlineIcon fontSize="small" />
                        </IconButton> */}
                        <IconButton onClick={() => handleFullDetailsView(row)} color="primary" size="small" title="Full Details">
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => handlePDFDownload(row)} color="primary" size="small" title="Download PDF">
                            <PictureAsPdfIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </TableCell>
            </TableRow>

            {/* Sub-Table Content */}
            <TableRow>
                {/* Updated colSpan from 9 to 10 because of new S.No column */}
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2, padding: 2, bgcolor: '#f7f9fc', borderRadius: '8px' }}>
                            <Typography variant="subtitle2" gutterBottom component="div" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                                Hearings Details
                            </Typography>
                            {row.hearings && row.hearings.length > 0 ? (
                                <Table size="small" aria-label="hearings">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                                            {/* 2. Hearing Sub-Table S.No Header */}
                                            <TableCell><strong>S.No</strong></TableCell>
                                            <TableCell><strong>Previous Hearing Date</strong></TableCell>
                                            <TableCell><strong>Next Hearing Date</strong></TableCell>
                                            <TableCell><strong>Court Room</strong></TableCell>
                                            <TableCell><strong>Judge</strong></TableCell>
                                            {/* <TableCell><strong>Status</strong></TableCell> */}
                                            <TableCell align="center"><strong>Action</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {row.hearings.map((h, hIndex) => (
                                            <TableRow key={h.id || hIndex} hover>
                                                {/* 3. Hearing Sub-Table S.No Value */}
                                                <TableCell sx={{ fontWeight: 'bold' }}>{hIndex + 1}</TableCell>
                                                <TableCell>
                                                    {h.hearing_date
                                                        ? new Date(h.hearing_date).toLocaleDateString("en-GB")
                                                        : "N/A"}
                                                </TableCell>

                                                <TableCell>
                                                    {h.next_hearing_date
                                                        ? new Date(h.next_hearing_date).toLocaleDateString("en-GB")
                                                        : "N/A"}
                                                </TableCell>
                                                <TableCell>{h.court_room || 'N/A'}</TableCell>
                                                <TableCell>{h.judge_name || 'N/A'}</TableCell>
                                                {/* <TableCell>
                                                    <Chip label={h.hearing_status || 'Pending'} size="small" color={h.hearing_status === 'Adjourned' ? 'warning' : 'primary'} />
                                                </TableCell> */}
                                                <TableCell align="center">
                                                    <IconButton size="small" color="secondary" onClick={() => onEditHearing(h, row)} title="Edit Hearing">
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" color="secondary" onClick={() => onViewHearingDetails(h, row)} title="View Hearing Details">
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    No hearings found for this case.
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

// --- MAIN COMPONENT ---
const CaseTable = ({ company_id, configServ, onEdit, change, setChange, companyName, companyLogo }) => {
    const [loading, setLoading] = useState(false);
    const [cases, setCases] = useState([]);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [perPage, setPerPage] = useState(50);

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedCaseId, setSelectedCaseId] = useState(1);
    const [selectedCaseNo, setSelectedCaseNo] = useState("");

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Debounce Logic Search
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            setSearch(searchInput);
            setPage(1);
        }, 600);
        return () => clearTimeout(delayDebounceFn);
    }, [searchInput]);

    const fetchAllCases = async () => {
        setLoading(true);
        try {
            const result = await configServ.getAllCases({
                company_id: company_id,
                page: page,
                per_page: perPage,
                search: search,
                status: statusFilter,
            });

            if (result.status) {
                const paginatedData = result.data;
                setCases(paginatedData.data || []);
                setTotalPages(paginatedData.last_page || 1);
            } else {
                setCases([]);
            }
        } catch (e) {
            console.error("Error fetching cases:", e);
            setCases([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllCases();
    }, [page, search, statusFilter, perPage, change]);

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const handleSaveHearing = async (payload) => {
        try {
            let response;

            if (isHearingEdit) {
                response = await configServ.updateHearing(HearingEditData.id, payload);
            } else {
                response = await configServ.createHearing(payload);
            }

            if (response.status) {
                setChange(!change);
                toast.success(
                    isHearingEdit
                        ? "Hearing updated successfully!"
                        : "Hearing saved successfully!"
                );

                setDialogOpen(false);
                setIsHearingEdit(false);
                setHearingEditData(null);
            }
        } catch (error) {
            console.error("Error saving hearing:", error);
            toast.error(
                isHearingEdit
                    ? "Failed to update hearing."
                    : "Failed to save hearing."
            );
        }
    };

    const handleAddHearingClick = (row) => {
        console.log("handleAddHearingClick", row);
        setSelectedCaseId(row.id);
        setSelectedCaseNo(row.case_no);
        setDialogOpen(true);
    };

    const [HearingEditData, setHearingEditData] = useState(null); // State to hold hearing data for editing
    const [isHearingEdit, setIsHearingEdit] = useState(false); // State to determine if it's an edit action

    // Hearing Edit Action Handler
    const handleEditHearing = (hearing, caseRow) => {
        console.log("Editing Hearing:", hearing, "from case:", caseRow);
        // toast.success(`Edit Hearing clicked for ID: ${hearing.id}`);
        setSelectedCaseId(caseRow.id);
        setHearingEditData(hearing);
        setDialogOpen(true);
        setIsHearingEdit(true);


        // Aap yahan apna hearing edit karne ka modal open karne ka code likh sakte hain
    };

    const [hearingDetailsOpen, setHearingDetailsOpen] = useState(false);
    const [viewingHearing, setViewingHearing] = useState(null);
    const [associatedCaseNo, setAssociatedCaseNo] = useState("");

    const handleViewHearingDetails = (hearing, caseRow) => {
        setViewingHearing(hearing);
        setAssociatedCaseNo(caseRow?.case_no || "");
        setHearingDetailsOpen(true);
    };

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [viewingCase, setViewingCase] = useState(null);

    const handleFullDetailsView = (row) => {
        setViewingCase(row);
        setDetailsOpen(true);
    };
    const handlePDFDownload = async (row) => {
        console.log("pdf", row);
        try {
            await DownloadCaseReportPdf(row, companyName, companyLogo);
            toast.success("PDF downloaded successfully");
        } catch (error) {
            console.error("Error downloading PDF:", error);
            toast.error("Failed to download PDF");
        }
    }
    const handlePerPageChange = (event) => {
        setPerPage(event.target.value);
        setPage(1);
    };

    return (
        <>

            <CaseDetailsDialog
                open={detailsOpen}
                handleClose={() => setDetailsOpen(false)}
                caseData={viewingCase}
            />

            <HearingDetailsDialog
                open={hearingDetailsOpen}
                handleClose={() => setHearingDetailsOpen(false)}
                hearingData={viewingHearing}
                caseNo={associatedCaseNo}
            />
            <Box sx={{ width: '100%', p: 2 }}>

                {/* 1. Filtering & Search Section */}
                <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search by Case No, Title, Client"
                            variant="outlined"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Filter by Status"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="Active">Active</MenuItem>
                            <MenuItem value="Pending">Pending</MenuItem>
                            <MenuItem value="Disposed">Disposed</MenuItem>
                            <MenuItem value="Closed">Closed</MenuItem>
                            <MenuItem value="On Hold">On Hold</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>

                {/* 2. Loading / Empty State */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : cases.length === 0 ? (
                    <Typography variant="body1" align="center" sx={{ py: 5, color: 'gray' }}>
                        No cases found.
                    </Typography>
                ) : isMobile ? (

                    /* 3. Mobile Screens Responsive Card + Accordion View */
                    <Grid container spacing={2}>
                        {cases.map((row) => (
                            <Grid item xs={12} key={row.id}>
                                <Card sx={{ borderLeft: '5px solid #1976d2', boxShadow: 2 }}>
                                    <CardContent sx={{ pb: '8px !important' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                                                {row.case_no}
                                            </Typography>
                                            <Box>
                                                <IconButton size="small" onClick={() => onEdit(row)} color="primary" title="Edit Case">
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                {/* <IconButton onClick={() => handleAddHearingClick(row)} color="primary" size="small" title="Add Hearing">
                                                    <AddCircleOutlineIcon fontSize="small" />
                                                </IconButton> */}
                                                <IconButton onClick={() => handleFullDetailsView(row)} color="primary" size="small" title="Full Details">
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton onClick={() => handlePDFDownload(row)} color="primary" size="small" title="Download PDF">
                                                    <PictureAsPdfIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>FIR No:</strong> {row.fir_no || 'N/A'}
                                        </Typography>

                                        <Box sx={{ mb: 1 }}>
                                            <strong style={{ fontSize: '14px' }}>Clients:</strong>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                {row.client_names && row.client_names.map((name, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={`${name} (${row.client_emails?.[index] || ''})`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        </Box>

                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Court:</strong> {row.court_name}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                                            <strong>Stage:</strong> {row.stage_name || 'N/A'}
                                        </Typography>

                                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                            <Chip label={row.status} size="small" color="primary" variant="outlined" />
                                            {/* <Chip label={row.priority} size="small" color={getPriorityColor(row.priority)} /> */}
                                        </Box>

                                        {/* Mobile ke liye Card ke andr chota Accordion for Hearings */}
                                        <Accordion sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Typography variant="body2" fontWeight="bold">Hearings ({row.hearings?.length || 0})</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ p: 1, bgcolor: '#fafafa' }}>
                                                {row.hearings && row.hearings.length > 0 ? (
                                                    row.hearings.map((h) => (
                                                        <Box key={h.id} sx={{ p: 1, mb: 1, borderBottom: '1px solid #e0e0e0' }}>
                                                            <Typography variant="caption" display="block">
                                                                <strong>Date:</strong>{" "}
                                                                {h.hearing_date
                                                                    ? new Date(h.hearing_date).toLocaleDateString("en-GB")
                                                                    : "N/A"}
                                                            </Typography>

                                                            <Typography variant="caption" display="block">
                                                                <strong>Next Date:</strong>{" "}
                                                                {h.next_hearing_date
                                                                    ? new Date(h.next_hearing_date).toLocaleDateString("en-GB")
                                                                    : "N/A"}
                                                            </Typography>
                                                            {/* <Typography variant="caption" display="block"><strong>Status:</strong> {h.hearing_status}</Typography> */}
                                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                                                                <Button size="small" variant="text" startIcon={<EditIcon style={{ fontSize: 14 }} />} onClick={() => handleEditHearing(h, row)}>
                                                                    Edit
                                                                </Button>
                                                                <Button size="small" variant="text" startIcon={<VisibilityIcon style={{ fontSize: 14 }} />} onClick={() => handleViewHearingDetails(h, row)}>
                                                                    View
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    ))
                                                ) : (
                                                    <Typography variant="caption" color="textSecondary">No hearings found.</Typography>
                                                )}
                                            </AccordionDetails>
                                        </Accordion>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (

                    /* 4. Desktop Table View (Aapka Pehle wala same Layout) */
                    <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: '8px' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                                    <TableCell width="50px" /> {/* Expand icon ke liye space */}
                                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>S.No</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Case No</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>FIR NO.</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Client Info (Multi)</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Court Name</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Opposite Party</TableCell>
                                    {/* <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Priority</TableCell> */}
                                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {cases.map((row, index) => (
                                    <Row
                                        key={row.id}
                                        row={row}
                                        index={index}
                                        onEdit={onEdit}
                                        onAddHearing={handleAddHearingClick}
                                        onEditHearing={handleEditHearing}
                                        handleFullDetailsView={handleFullDetailsView}
                                        handlePDFDownload={handlePDFDownload}
                                        onViewHearingDetails={handleViewHearingDetails}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* 5. Pagination Control */}
                {/* 5. Pagination Control */}
                {!loading && cases.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Stack
                            direction={{ xs: "column", sm: "column", md: "row" }}
                            justifyContent="end"
                            alignItems={{ xs: "stretch", md: "center" }}
                            spacing={2}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: { xs: "center", md: "flex-start" },
                                    gap: 1,
                                }}
                            >
                                <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                                    Rows per page:
                                </Typography>

                                <FormControl size="small" sx={{ minWidth: 90 }}>
                                    <Select
                                        value={perPage}
                                        onChange={handlePerPageChange}
                                    >
                                        <MenuItem value={50}>50</MenuItem>
                                        <MenuItem value={100}>100</MenuItem>
                                        <MenuItem value={200}>200</MenuItem>
                                        <MenuItem value={500}>500</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: { xs: "center", md: "flex-end" },
                                }}
                            >
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={handlePageChange}
                                    color="primary"
                                    variant="outlined"
                                    shape="rounded"
                                    siblingCount={1}
                                    boundaryCount={1}
                                />
                            </Box>
                        </Stack>
                    </Box>
                )}
            </Box>

            <HearingDialog
                open={dialogOpen}
                handleClose={() => {
                    setDialogOpen(false);
                    setIsHearingEdit(false);
                    setHearingEditData(null);
                }}
                case_id={selectedCaseId}
                case_no={selectedCaseNo}
                onSave={handleSaveHearing}
                HearingEditData={HearingEditData}
                isHearingEdit={isHearingEdit}
                setIsHearingEdit={setIsHearingEdit}
            />
        </>
    );
};

export default CaseTable;